import { useState, useCallback, useEffect } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';

export interface DocumentItem {
  id: string;
  hash: string;
  s3Key?: string;
  s3Url: string; // Always present in API response
  path?: string; // Derived from s3Url
  thumbnail?: string; // Derived from s3Url
  title?: string;
  description?: string;
  category?: string | null;
  amount?: string | number; // API returns as string
  timestamp?: number;
  createdAt: number; // Always present in API response
  created_at?: number | string;
  paymentDate?: string | number;
  endDate?: string;
  startDate?: string;
  threadId: string; // Always present in API response
  diaryId?: string; // Present in API response
  content?: Array<{
    type: string;
    text?: string;
    path?: string;
    hash?: string;
    thumbnail?: string;
    s3Url?: string;
  }>; // Generated from API data for compatibility
  lastAssistantMessage?: string;
  lastAssistantMessageTimestamp?: number;
  last_read_data?: string | { user_id: string; timestamp: number };
  type?: string;
  status?: string;
  state?: string;
  interpretationObj?: any;
  highlightedTitle?: string;
  highlightedContent?: string;
  source: 'legacy' | 'v2'; // Always present in API response
  // Additional fields from the /api/documents endpoint
  imageId?: string;
  details?: string;
  summary?: string;
  counterpart?: string;
  vatDetails?: string | any; // API returns as JSON string
  s3UrlStraightened?: string;
  straightenedHelpful?: string | boolean; // API returns as string
  isPhoto?: string | boolean; // API returns as string
  modifiedAt?: number | string;
  uploadedBy?: string;
}

export const useDocuments = () => {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeOrganisation] = useActiveOrganisation();

  const fetchDocuments = useCallback(async () => {
    if (!activeOrganisation?.organizationId) return;

    try {
      // Get access token
      const tokenResponse = await fetch('/access-token');
      const { accessToken } = await tokenResponse.json();

      const response = await fetch('/api/documents', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-Organization': activeOrganisation.organizationId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const rawData = await response.json();

      // Transform the API response to be compatible with existing components
      const transformedData = rawData.map((item: any): DocumentItem => {
        // Use the actual path and thumbnail from API response, with fallbacks
        const path = item.s3Url.replace('s3://', '') || '';
        const thumbnail =
          `${item.s3Url.replace('s3://', '')}/thumbnail.jpg` || '';

        // Create content array for compatibility with existing components
        const content = [
          {
            type: 'image',
            path,
            hash: item.hash,
            thumbnail,
            s3Url: item.s3Url,
          },
        ];

        // Parse vatDetails if it's a string
        let parsedVatDetails = item.vatDetails;
        if (typeof item.vatDetails === 'string') {
          try {
            parsedVatDetails = JSON.parse(item.vatDetails);
          } catch (e) {
            // Keep as string if parsing fails
          }
        }

        return {
          ...item,
          // Add derived fields for compatibility
          path,
          thumbnail,
          content,
          title: item.summary || item.counterpart || 'Document',
          description: item.details || item.summary,
          // Normalize data types
          amount: item.amount
            ? typeof item.amount === 'string'
              ? parseFloat(item.amount)
              : item.amount
            : undefined,
          vatDetails: parsedVatDetails,
          straightenedHelpful:
            item.straightenedHelpful === '1' ||
            item.straightenedHelpful === true,
          isPhoto: item.isPhoto === '1' || item.isPhoto === true,
          // Use createdAt as timestamp if timestamp is missing
          timestamp: item.timestamp || item.createdAt,
          created_at: item.createdAt,
        };
      });

      setDocuments(transformedData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [activeOrganisation?.organizationId]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(true);
    fetchDocuments();
  }, [fetchDocuments]);

  // Refetch function
  const refetch = useCallback(async () => {
    setIsRefetching(true);
    await fetchDocuments();
    setIsRefetching(false);
  }, [fetchDocuments]);

  return {
    documents,
    error,
    refetch,
    isRefetching,
    isLoading,
  };
};
